import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import trainService from '../services/trainService';
import bookingService from '../services/bookingService';
import SeatSelector from '../components/SeatSelector';
import { formatCurrency } from '../utils/helpers';

const SeatSelectionPage = () => {
  const { trainId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [train, setTrain] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  const { state } = location;
  const journeyDate = state?.journeyDate;
  const numPassengers = state?.passengers || 1;

  useEffect(() => {
    const passengerList = [];
    for (let i = 0; i < numPassengers; i++) {
      passengerList.push({
        id: `passenger_${i}`,
        name: '',
        age: '',
        category: 'Adult',
        gender: 'Male',
      });
    }
    setPassengers(passengerList);

    let isMounted = true;

    const fetchTrain = async () => {
      try {
        const trainData = await trainService.getTrainDetails(trainId);
        if (isMounted) {
          setTrain(trainData);
        }
      } catch (error) {
        console.error('Error fetching train:', error);
        alert('Error loading train details');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTrain();

    return () => {
      isMounted = false;
    };
  }, [numPassengers, trainId]);

  const handleSeatSelect = (seat, action) => {
    if (action === 'add') {
      if (selectedSeats.length < numPassengers) {
        setSelectedSeats([...selectedSeats, seat]);
      } else {
        alert(`You can only select ${numPassengers} seats`);
      }
    } else if (action === 'remove') {
      setSelectedSeats(selectedSeats.filter(
        s => !(s.coachNumber === seat.coachNumber && s.seatNumber === seat.seatNumber)
      ));
    }
  };

  const handlePassengerChange = (idx, field, value) => {
    const updatedPassengers = [...passengers];
    updatedPassengers[idx][field] = value;
    setPassengers(updatedPassengers);
  };

  const calculateTotalFare = () => {
    return (train?.baseFare || 0) * selectedSeats.length;
  };

  const handleConfirmBooking = async () => {
    // Validation
    if (selectedSeats.length !== numPassengers) {
      alert(`Please select ${numPassengers} seats`);
      return;
    }

    // Check if all passengers have names
    if (passengers.some(p => !p.name)) {
      alert('Please enter name for all passengers');
      return;
    }

    setBooking(true);

    try {
      const pnr = await bookingService.createBooking({
        userId: user.uid,
        trainId: train.trainId,
        passengers: passengers.map((p, idx) => ({
          ...p,
          id: `${p.name}_${idx}`,
          seatNumber: selectedSeats[idx].seatNumber,
          coachNumber: selectedSeats[idx].coachNumber,
          status: 'confirmed',
        })),
        seats: selectedSeats,
        totalFare: calculateTotalFare(),
        journeyDate: journeyDate,
        paymentId: `PAY_${Date.now()}`,
      });

      // Show confirmation
      alert(`Booking confirmed! PNR: ${pnr}`);
      navigate('/bookings');
    } catch (error) {
      console.error('Booking error:', error);
      alert('Error creating booking: ' + error.message);
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!train) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg text-red-600">Train not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Select Seats & Enter Passenger Details</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Seat Selector */}
          <div className="lg:col-span-2">
            <SeatSelector
              coaches={train.seats}
              onSeatSelect={handleSeatSelect}
              selectedSeats={selectedSeats}
            />
          </div>

          {/* Passenger Details & Summary */}
          <div className="space-y-6">
            {/* Booking Summary */}
            <div className="bg-white p-6 rounded-lg shadow-md sticky top-20">
              <h3 className="text-xl font-bold mb-4">Booking Summary</h3>

              <div className="space-y-3 mb-6 text-sm">
                <div>
                  <p className="text-gray-600">Train</p>
                  <p className="font-semibold">{train.trainName}</p>
                </div>
                <div>
                  <p className="text-gray-600">Selected Seats</p>
                  <p className="font-semibold">{selectedSeats.length} / {numPassengers}</p>
                </div>
                <div>
                  <p className="text-gray-600">Fare per Seat</p>
                  <p className="font-semibold">{formatCurrency(train.baseFare)}</p>
                </div>
              </div>

              <div className="border-t pt-4 mb-4">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">Total Fare:</span>
                  <span className="text-xl font-bold text-accent">{formatCurrency(calculateTotalFare())}</span>
                </div>
              </div>

              <button
                onClick={handleConfirmBooking}
                disabled={booking || selectedSeats.length !== numPassengers}
                className="w-full bg-primary text-white py-3 rounded-lg hover:bg-blue-700 font-semibold transition disabled:opacity-50"
              >
                {booking ? 'Processing...' : 'Confirm Booking'}
              </button>
            </div>
          </div>
        </div>

        {/* Passenger Details Form */}
        <div className="mt-8 bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-6">Passenger Details</h2>

          <div className="space-y-6">
            {passengers.map((passenger, idx) => (
              <div key={idx} className="border-b pb-6">
                <h3 className="font-semibold mb-4">Passenger {idx + 1}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={passenger.name}
                    onChange={(e) => handlePassengerChange(idx, 'name', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                    required
                  />
                  <input
                    type="number"
                    placeholder="Age"
                    value={passenger.age}
                    onChange={(e) => handlePassengerChange(idx, 'age', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  />
                  <select
                    value={passenger.gender}
                    onChange={(e) => handlePassengerChange(idx, 'gender', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  <select
                    value={passenger.category}
                    onChange={(e) => handlePassengerChange(idx, 'category', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  >
                    <option value="Adult">Adult</option>
                    <option value="Child">Child</option>
                    <option value="Senior Citizen">Senior Citizen</option>
                  </select>
                </div>
                {selectedSeats[idx] && (
                  <p className="text-sm text-gray-600 mt-2">
                    Seat: Coach {selectedSeats[idx].coachNumber} - Seat {selectedSeats[idx].seatNumber}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeatSelectionPage;
